import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useI18n } from "../../i18n/I18nContext";
import { useAuth } from "../../context/AuthContext";
import { api, apiError } from "../../lib/api";
import { Logo } from "../../components/Logo";
import { LanguageToggle } from "../../components/LanguageToggle";
import { toast } from "sonner";

export default function Login() {
  const { t, isAr } = useI18n();
  const { loginWithToken } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("customer");
  const [phone, setPhone] = useState("");
  const [channel, setChannel] = useState("whatsapp");
  const [otpSent, setOtpSent] = useState(false);
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [demoCode, setDemoCode] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const requestOtp = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/otp/request", { phone, channel });
      setOtpSent(true);
      if (data.demo_code) { setDemoCode(data.demo_code); toast.info(`${t("auth.demo_code")}: ${data.demo_code}`); }
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };
  const verifyOtp = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/otp/verify", { phone, code, name });
      loginWithToken(data.token, { ...data.customer, kind: "customer" });
      toast.success(t("auth.welcome"));
      nav("/account");
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };
  const staffLogin = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/staff/login", { email, password });
      loginWithToken(data.token, { ...data.user, kind: "staff" });
      toast.success(t("auth.welcome"));
      nav("/admin");
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 px-4">
      <div className="absolute top-4 end-4"><LanguageToggle /></div>
      <div className="w-full max-w-md">
        <div className="mb-6 flex justify-center"><Logo height={48} /></div>
        <div className="rounded-3xl border border-border bg-white p-7 shadow-sm">
          <div className="mb-6 grid grid-cols-2 gap-2 rounded-full bg-slate-100 p-1">
            <button onClick={() => setTab("customer")} data-testid="tab-customer" className={`rounded-full py-2 text-sm font-semibold ${tab === "customer" ? "bg-white text-primary shadow" : "text-slate-500"}`}>{t("auth.customer_tab")}</button>
            <button onClick={() => setTab("staff")} data-testid="tab-staff" className={`rounded-full py-2 text-sm font-semibold ${tab === "staff" ? "bg-white text-primary shadow" : "text-slate-500"}`}>{t("auth.staff_tab")}</button>
          </div>

          {tab === "customer" ? (
            <div className="space-y-4">
              {!otpSent ? (
                <>
                  <div><label className="text-sm font-medium text-slate-700">{t("auth.enter_phone")}</label>
                    <input value={phone} onChange={(e) => setPhone(e.target.value)} dir="ltr" placeholder="05xxxxxxxx" data-testid="login-phone" className="mt-1 w-full rounded-xl border border-border px-4 py-3" /></div>
                  <div className="flex gap-2">
                    {[["whatsapp", t("auth.channel_wa")], ["telegram", t("auth.channel_tg")], ["sms", t("auth.channel_sms")]].map(([v, l]) => (
                      <button key={v} onClick={() => setChannel(v)} className={`flex-1 rounded-lg border py-2 text-xs font-medium ${channel === v ? "border-primary bg-primary/5 text-primary" : "border-border text-slate-500"}`}>{l}</button>
                    ))}
                  </div>
                  <button onClick={requestOtp} disabled={busy || !phone} data-testid="send-otp-btn" className="w-full rounded-full bg-primary py-3 font-semibold text-white disabled:opacity-50">{busy ? t("common.loading") : t("auth.send_code")}</button>
                </>
              ) : (
                <>
                  {demoCode && <div className="rounded-xl bg-amber-50 p-3 text-center text-sm text-amber-800" data-testid="demo-code-box"><b>{t("auth.demo_code")}: {demoCode}</b><div className="mt-1 text-xs">{t("auth.otp_demo_hint")}</div></div>}
                  <input value={code} onChange={(e) => setCode(e.target.value)} dir="ltr" placeholder="000000" data-testid="login-otp" className="w-full rounded-xl border border-border px-4 py-3 text-center text-lg tracking-widest" />
                  <input value={name} onChange={(e) => setName(e.target.value)} placeholder={t("common.name")} data-testid="login-name" className="w-full rounded-xl border border-border px-4 py-3" />
                  <button onClick={verifyOtp} disabled={busy || !code} data-testid="verify-otp-btn" className="w-full rounded-full bg-primary py-3 font-semibold text-white disabled:opacity-50">{busy ? t("common.loading") : t("auth.verify")}</button>
                </>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              <input value={email} onChange={(e) => setEmail(e.target.value)} dir="ltr" placeholder={t("common.email")} data-testid="staff-email" className="w-full rounded-xl border border-border px-4 py-3" />
              <input value={password} onChange={(e) => setPassword(e.target.value)} type="password" dir="ltr" placeholder={t("auth.password")} data-testid="staff-password" className="w-full rounded-xl border border-border px-4 py-3" />
              <button onClick={staffLogin} disabled={busy || !email || !password} data-testid="staff-login-btn" className="w-full rounded-full bg-primary py-3 font-semibold text-white disabled:opacity-50">{busy ? t("common.loading") : t("auth.staff_login")}</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
